import { Header } from '../components/Header';
import { PositionsTable } from '../components/PositionsTable';
import { TradingChart } from '../components/TradingChart';
import { OrderPanel } from '../components/OrderPanel';
import { AssetsSummary } from '../components/AssetsSummary';
import { PriceRefreshTimer } from '../components/PriceRefreshTimer';
import { TSLStatusCounter } from '../components/TSLStatusCounter';
import { useTradingState } from '../hooks/useMexcApi';
import { usePricePolling } from '../hooks/usePricePolling';
import { useView } from '../context/ViewContext';
import { calculateSafePnL } from '../utils/pnlCalculations';
import { useTrailingStopLoss } from '../hooks/useTrailingStopLoss';

export function Dashboard() {
  const { selectedView, setSelectedView } = useView();
  const tradingState = useTradingState();
  const priceData = usePricePolling();

  // Initialize TSL engine - this must be called at Dashboard level
  // to ensure the engine starts regardless of which view is active
  const { isEngineRunning, engineStatus } = useTrailingStopLoss();

  // Ensure we have valid data with fallbacks
  const positions = tradingState.positions || [];
  const assets = tradingState.assets || [];
  const isLoading = tradingState.isLoading;
  const connectionStatus = tradingState.connectionStatus;
  const isUsingCache = tradingState.isUsingCache;

  // Debug selected symbol (guarded)
  if (import.meta.env.DEV && localStorage.getItem('debug_ui') === 'true') {
    console.log('🎯 Dashboard Debug:', {
      selectedSymbol: tradingState.selectedSymbol,
      positionsCount: positions.length,
      positionSymbols: positions.map(p => p.symbol),
      isLoading,
      isConnected: connectionStatus?.isConnected,
      isUsingCache,
      tslEngineRunning: isEngineRunning,
      tslEngineStatus: engineStatus
    });
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header
        selectedView={selectedView}
        onViewChange={setSelectedView}
      />

      <main className="container mx-auto px-4 py-6 space-y-6">
        {selectedView === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Trading Chart */}
              <div className="lg:col-span-2">
                {tradingState.selectedSymbol ? (
                  <TradingChart
                    symbol={tradingState.selectedSymbol}
                    positions={positions.filter(p => p.symbol === tradingState.selectedSymbol)}
                  />
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 h-96 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-gray-500 dark:text-gray-400 mb-2">
                        Select a symbol to view chart
                      </p>
                      {positions.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-2 mt-4">
                          {positions.slice(0, 6).map((p) => (
                            <button
                              key={p.symbol}
                              onClick={() => tradingState.selectSymbol(p.symbol)}
                              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                            >
                              {p.symbol}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Price Refresh Timer & TSL Status */}
              <div className="lg:col-span-1 space-y-4">
                <PriceRefreshTimer />
                <TSLStatusCounter positions={positions} />
                <OrderPanel
                  selectedSymbol={tradingState.selectedSymbol}
                  positions={positions}
                  assets={assets}
                  onSymbolSelect={tradingState.selectSymbol}
                />
              </div>
            </div>

            {/* Assets Summary */}
            <AssetsSummary
              assets={assets}
              isLoading={isLoading}
            />
          </div>
        )}

        {selectedView === 'trading' && (
          <div className="space-y-6">
            {/* Price Refresh Status Bar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Live Price Updates
                  </h3>
                  <PriceRefreshTimer compact={true} />
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Prices refresh automatically every 1 minute for accurate TSL decisions
                </div>
              </div>
            </div>

            {/* Symbol Selection */}
            {!tradingState.selectedSymbol && positions.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Select a Symbol to Trade
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {positions.map((position) => {
                    // Calculate PnL safely using utility function with polled ticker data
                    const pnl = calculateSafePnL(position, priceData.tickers);

                    return (
                      <button
                        key={position.symbol}
                        onClick={() => tradingState.selectSymbol(position.symbol)}
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-center"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {position.symbol}
                        </div>
                        <div className={`text-sm ${pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trading Chart */}
              <div>
                {tradingState.selectedSymbol ? (
                  <TradingChart
                    symbol={tradingState.selectedSymbol}
                    positions={positions.filter(p => p.symbol === tradingState.selectedSymbol)}
                  />
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 h-96 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-gray-500 dark:text-gray-400 mb-2">
                        Select a symbol to start trading
                      </p>
                      {positions.length === 0 && (
                        <p className="text-sm text-gray-400">
                          No active positions found
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Panel */}
              <div>
                <OrderPanel
                  selectedSymbol={tradingState.selectedSymbol}
                  positions={positions}
                  assets={assets}
                  onSymbolSelect={tradingState.selectSymbol}
                />
              </div>
            </div>

            {/* Assets Summary */}
            <AssetsSummary
              assets={assets}
              isLoading={isLoading}
            />
          </div>
        )}

        {selectedView === 'positions' && (
          <div className="space-y-6">
            {/* Price Refresh Status Bar */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Position Price Updates
                  </h3>
                  <PriceRefreshTimer compact={true} />
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Real-time P&L calculations with 1-minute price refresh cycles
                </div>
              </div>
            </div>

            {/* Detailed Positions View */}
            <PositionsTable
              positions={positions}
              isLoading={isLoading}
              onSelectPosition={(position) => tradingState.selectSymbol(position.symbol)}
              detailed={true}
              connectionStatus={connectionStatus}
              isUsingCache={isUsingCache}
            />

            {/* Assets Summary */}
            <AssetsSummary
              assets={assets}
              isLoading={isLoading}
            />
          </div>
        )}

        {selectedView === 'charts' && (
          <div className="space-y-6">
            {/* Chart View */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              {tradingState.selectedSymbol ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {tradingState.selectedSymbol} Chart
                    </h3>
                    <PriceRefreshTimer compact={true} />
                  </div>
                  <TradingChart
                    symbol={tradingState.selectedSymbol}
                    positions={positions.filter(p => p.symbol === tradingState.selectedSymbol)}
                    height={500}
                  />
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Select a Symbol
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Choose a symbol from your positions to view its chart
                  </p>
                  {positions.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2">
                      {positions.slice(0, 8).map((p) => (
                        <button
                          key={p.symbol}
                          onClick={() => tradingState.selectSymbol(p.symbol)}
                          className="px-4 py-2 text-sm bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors font-medium"
                        >
                          {p.symbol}
                        </button>
                      ))}
                    </div>
                  )}
                  {positions.length === 0 && (
                    <p className="text-sm text-gray-400">
                      No active positions found
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
